import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, Renderer2, signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CmgUiUtilities } from 'apps/cmg-clear2-ui/src/app/cmg-1003/components/common/cmg-ui-utils';
import { Cmg1003LookupsModel } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models';
import { Cmg1003LookupsQuery, SecurityProfileQuery } from 'apps/cmg-clear2-ui/src/app/loan-state';
import { SecurityProfileModel, SecurityProfileRole } from 'apps/cmg-clear2-ui/src/app/loan-state/security-profile';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError, combineLatest, EMPTY, filter, map, take } from 'rxjs';
import { addPartnerErrors, loProfiles, oktaEmailValidator } from '../../utils/partners.utils';
import { Models } from '../../../../models/global.models';
import { RouteApiService } from '../../../../stores/api';
import { AddPartnerData, PartnersService } from 'apps/cmg-clear2-ui/src/app/shared';
import { RouteUiService } from '../../../../stores/ui/route-ui.service';
import { ConfirmationModalCloseType } from 'apps/cmg-clear2-ui/src/app/models/global.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationService } from 'apps/cmg-clear2-ui/src/app/services';

import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { CmgFormControlDomDirective } from '../../../../../shared/directives/cmg-form-control-dom.directive';
import { PrimeTemplate } from 'primeng/api';
import { CmgValidatorMessagesComponent } from '../../../../../shared/components/cmg-validator-messages/cmg-validator-messages.component';
import { Tooltip } from 'primeng/tooltip';
import { InputText } from 'primeng/inputtext';
import { ButtonDirective } from 'primeng/button';
import { QaSlugPipe } from '../../../../../shared/pipes/qa-slug.pipe';

@Component({
  selector: 'cmg-clear2-ui-add-partner-modal',
  templateUrl: './add-partner-modal.component.html',
  styleUrls: ['./add-partner-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    Message,
    Select,
    CmgFormControlDomDirective,
    PrimeTemplate,
    CmgValidatorMessagesComponent,
    Tooltip,
    InputText,
    ButtonDirective,
    QaSlugPipe,
  ],
})
export class AddPartnerModalComponent implements OnInit {
  private readonly _api = inject(RouteApiService);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _dialogConfig = inject(DynamicDialogConfig);
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _el = inject(ElementRef);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _lookupsQuery = inject(Cmg1003LookupsQuery);
  private readonly _notificationService = inject(NotificationService);
  private readonly _partnerService = inject(PartnersService);
  private readonly _renderer = inject(Renderer2);
  private readonly _routeUiService = inject(RouteUiService);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);

  addPartnerForm: FormGroup;
  showLoanOfficer = false;
  isLoading = signal(false);
  loanOfficers: Models.LoanOfficer[];
  autoIdPrefix = 'addPartner-';

  partnerTypeOptions = [
    {
      id: 0,
      value: 'Real Estate Agent',
      name: 'Real Estate Agent',
    },
  ];

  addPartnerError: string;

  ngOnInit() {
    this.addPartnerForm = this._formBuilder.group(
      {
        loanOfficer: [],
        partnerType: [{ value: this.partnerTypeOptions[0].id, disabled: true }],
        email: ['', [Validators.required, oktaEmailValidator()]],
      },
      { updateOn: 'blur' },
    );

    const partnerType = this._dialogConfig.data?.partnerType;
    if (partnerType) {
      this.addPartnerForm.controls.partnerType.setValue(partnerType, { emitEvent: false });
    }

    const loanOfficerNmls = this._dialogConfig.data?.loanOfficerNmls;
    if (loanOfficerNmls) {
      this.addPartnerForm.controls.loanOfficer.setValue(loanOfficerNmls, { emitEvent: false });
    }

    const email = this._dialogConfig.data?.accountEmail;
    if (email) {
      this.addPartnerForm.controls.email.setValue(email, { emitEvent: false });
    }

    this._initializeLoanOfficerDropdown();
  }

  closeModal() {
    this._dialogRef.close();
  }

  addPartner() {
    CmgUiUtilities.markFormAsDirty(this.addPartnerForm);
    CmgUiUtilities.markFormAsTouched(this.addPartnerForm);

    if (this.addPartnerForm.valid) {
      this.isLoading.set(true);
      this.addPartnerForm.disable({ emitEvent: false });
      this.addPartnerError = '';
      const selectedLoanOfficer = this.addPartnerForm.controls.loanOfficer.value;

      this._partnerService
        .getPartnerInfo(this.addPartnerForm.controls.email.value, selectedLoanOfficer)
        .pipe(
          catchError((err) => {
            this.isLoading.set(false);
            this.addPartnerForm.enable({ emitEvent: false });

            if (err?.error?.Title === 'NotFound') {
              this.closeModal();
              this._partnerService.showAddPartnerNewAccountModal(
                this.addPartnerForm.controls.email.value,
                this.addPartnerForm.controls.partnerType.value,
                selectedLoanOfficer,
                this._routeUiService,
              );
            } else {
              this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
            }

            return EMPTY;
          }),
        )
        .subscribe((resp: AddPartnerData) => {
          this.isLoading.set(false);
          if (resp?.partnerDetails) {
            const activePartner = resp?.partnerDetails.find((p) => p.isCobrandingActive && !p.isOutsideCurrentLoPipeline);
            const inactivePartner = resp?.partnerDetails.find((p) => !p.isCobrandingActive && !p.isOutsideCurrentLoPipeline);
            const outsidePipelinePartner = resp?.partnerDetails.find((p) => p.isOutsideCurrentLoPipeline);

            if (activePartner) {
              this.addPartnerError = addPartnerErrors.associatedWithActivePartner;
            } else if (inactivePartner) {
              this.addPartnerError = addPartnerErrors.associatedWithInactivePartner;
            } else if (outsidePipelinePartner) {
              // Hide the parent "Add Partner" modal
              const dialogEl = this._el.nativeElement.closest('.p-dialog');
              this._renderer.addClass(dialogEl, 'd-none');

              const dialogRef = this._partnerService.showAddPartnerExistingAccountModal(outsidePipelinePartner);
              dialogRef.onClose.pipe(takeUntilDestroyed(this._destroyRef), take(1)).subscribe((value: ConfirmationModalCloseType) => {
                if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
                  this._partnerService
                    .inviteSinglePartner(outsidePipelinePartner.guid, selectedLoanOfficer)
                    .pipe(
                      catchError(() => {
                        this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
                        return EMPTY;
                      }),
                    )
                    .subscribe((resp) => {
                      this.closeModal();
                      if (resp.successful) {
                        this._notificationService.showToastSub.next({
                          severity: 'success',
                          summary: 'Partner successfully added:',
                          detail: `Real Estate Agent, ${outsidePipelinePartner.firstName} ${outsidePipelinePartner.lastName}`,
                        });
                        this._routeUiService.refreshGrid$.next(this._routeUiService.refreshGrid$.value + 1);
                      } else {
                        this._notificationService.showToast(Models.DefaultPartnersErrorMessage.createEmptyModel());
                      }
                    });
                } else {
                  // Reshow the "Add Partner" modal
                  this._renderer.removeClass(dialogEl, 'd-none');
                }
              });
            }

            this.addPartnerForm.enable({ emitEvent: false });
            this.addPartnerForm.controls.partnerType.disable({ emitEvent: false });
            this._cdRef.detectChanges();
          }
        });
    }
  }

  private _initializeLoanOfficerDropdown() {
    combineLatest({
      loanOfficers: this._api.loanOfficers$.pipe(
        map((response) => {
          return (response ?? []).filter((lo) => !!lo.fullname && !!lo.nmlsid);
        }),
      ),
      activeProfile: this._securityProfileQuery.selectActive().pipe(filter((t) => !!t)),
      securityProfileLookup: this._lookupsQuery.select().pipe(
        filter((t) => {
          return !!t && Object.keys(t).length > 0;
        }),
      ),
    })
      .pipe(take(1))
      .subscribe((value: { loanOfficers: Models.LoanOfficer[]; activeProfile: SecurityProfileModel; securityProfileLookup: Cmg1003LookupsModel }) => {
        this.loanOfficers = value.loanOfficers;
        const securityProfileLookup = value.securityProfileLookup?.securityProfileRoles?.find(
          (securityProfileRole) => Number(securityProfileRole.id) === Number(value.activeProfile.securityProfileId),
        );
        const securityProfileRole = securityProfileLookup?.value as SecurityProfileRole;

        if (!loProfiles.includes(securityProfileRole)) {
          this.showLoanOfficer = true;
          this.addPartnerForm.controls.loanOfficer.addValidators(Validators.required);
          this.addPartnerForm.controls.loanOfficer.updateValueAndValidity({ emitEvent: false });
        } else {
          const nmlsId = this.loanOfficers.find((lo) => lo.username === value.activeProfile.userName)?.nmlsid;
          this.addPartnerForm.controls.loanOfficer.setValue(nmlsId, { emitEvent: false });
        }
      });
  }
}
